import { lbsToKg } from '@cf-shared/shared-util';
import { FitnessState } from '@cf-shared/data-model/user-state';

/**
 * The sensible fallback step size for the temporary "automatic" unbrowse equipment (see
 * `resolveUsableCustomWeightIncrement_kg` below, and `computeUnbrowsedCustomWeightIncrement_kg` in
 * `pages/workout-detail/workout-progression-decision.ts`) whenever there is no usable increment to
 * infer — either because there is no previous session to compare against, or because the
 * inferred/stored delta itself is not a valid step (zero, negative, or NaN, per #4337). Lives in its
 * own small, dependency-free file here in `util/` (not a page-level file, and not
 * `services/equipment-helpers.ts`) because both a page and a plain model class need it — a model
 * must never import from a page — and because `services/equipment-helpers.ts` pulls in the
 * i18n/Angular service chain for its other exports, which this pure arithmetic has no reason to drag
 * into either caller. This mirrors `util/orphan-subslot-reporter.ts`: a pure, DI-free module that
 * several Angular-aware callers share. Shared by every site that needs this default so the two unit
 * values can't silently drift apart from each other.
 */
export function defaultCustomWeightIncrement_kg(incrementUnit: 'kg' | 'lb'): number {
  return incrementUnit === 'lb' ? lbsToKg(5) : 2.5;
}

/**
 * How far a step size may sit off a round value before the app stops believing it describes a real
 * machine (#5344).
 *
 * Deliberately NOT `WEIGHT_GRID_TOLERANCE_KG` (0.05): that one forgives pound-to-kilogram residue
 * in a WEIGHT, and at 0.05 kg it keeps every bad step this ticket repairs. The margins that decide
 * this value are in `design-docs/reactive-deload.md`, the #4337 section.
 */
export const EQUIPMENT_STEP_ROUNDNESS_TOLERANCE_KG = 0.005;

/**
 * The coarsest grid every real equipment step lands on, counted in whichever unit the machine is
 * built in — which is why this constant carries no unit suffix, and why the caller converts it for
 * the pounds pass. Every step anybody's gym actually has is a whole number of quarter-kilos (0.25,
 * 0.5, 1, 1.25, 2, 2.5, 5, 10 kg) or a whole number of quarter-pounds (0.25, 0.5, 1, 2.5, 5, 10 lb).
 * A guessed step that is neither is the leftover of two weights that were never a step apart.
 */
const EQUIPMENT_STEP_GRID_PER_UNIT = 0.25;

/** Is `value_kg` a whole multiple (at least one) of `grid_kg`, within the roundness tolerance? */
function isNearWholeMultipleOfGrid(value_kg: number, grid_kg: number): boolean {
  const positions = Math.round(value_kg / grid_kg);
  if (positions < 1) {
    return false;
  }
  return Math.abs(value_kg - positions * grid_kg) <= EQUIPMENT_STEP_ROUNDNESS_TOLERANCE_KG;
}

/**
 * Could `step_kg` be the step size of a machine somebody actually trains on? (#5344)
 *
 * Three things disqualify a step:
 *
 * 1. It is not a finite number above zero — #4337's original rule, unchanged.
 * 2. It is round in neither kilograms nor pounds. Both grids are always checked, whichever unit key
 *    the value was stored under, because the stored value is ALWAYS in kilograms: a pounds machine
 *    records `lbsToKg(5)`, not `5`. Checking only the key's own grid would repair every genuine
 *    pounds step on a kilogram-displaying account.
 * 3. It is at or above the weight it has to load, when a caller knows that weight. No machine has a
 *    single position at or below the weight somebody trains at.
 *
 * `workingWeight_kg` is optional because the callers differ in what they know: the boot migration
 * reads stored state with no workout in hand and can judge roundness only, while
 * `UiExercise.checkEquipment` has the exercise's last logged weight and passes it.
 *
 * Why these rules and not #5325's load check, and the production values each one rejects:
 * `design-docs/reactive-deload.md`, the #4337 section.
 */
export function isPlausibleEquipmentStep_kg(step_kg: number, workingWeight_kg?: number): boolean {
  if (!Number.isFinite(step_kg) || step_kg <= 0) {
    return false;
  }
  if (!isNearWholeMultipleOfGrid(step_kg, EQUIPMENT_STEP_GRID_PER_UNIT) &&
      !isNearWholeMultipleOfGrid(step_kg, lbsToKg(EQUIPMENT_STEP_GRID_PER_UNIT))) {
    return false;
  }
  // only compare against a working weight the caller could actually establish
  if (workingWeight_kg !== undefined && Number.isFinite(workingWeight_kg) && workingWeight_kg > 0 &&
      step_kg >= workingWeight_kg) {
    return false;
  }
  return true;
}

/**
 * Must the read-time heal replace `step_kg`? (#6295)
 *
 * The same answer as `isPlausibleEquipmentStep_kg`, with one exception: a step no bigger than the
 * app's own largest default (2.5 kg) that fails ONLY the "at or above the working weight" rule is
 * kept. The heal would replace it with a default that fails the same rule, so replacing it could
 * never reach a valid step. It only reported a PROBLEM on every app start for a cable lateral raise
 * trained at 2.5 kg, and on a pounds display it swapped a 2.5 kg step for 5 lb.
 *
 * Deliberately NOT folded into `isPlausibleEquipmentStep_kg`: `resolveCustomWeightIncrementSource`
 * asks that predicate which unit's step to use, and there a stored 2.5 kg must still lose to a
 * genuine 1 kg step in the other unit when the exercise is trained at 2 kg.
 */
export function needsCustomWeightIncrementHeal(step_kg: number, workingWeight_kg?: number): boolean {
  if (isPlausibleEquipmentStep_kg(step_kg, workingWeight_kg)) {
    return false;
  }
  const failsOnlyTheLoadRule = isPlausibleEquipmentStep_kg(step_kg);
  return !(failsOnlyTheLoadRule && step_kg <= defaultCustomWeightIncrement_kg('kg'));
}

/**
 * The step size to actually use for the temporary "automatic" unbrowse equipment, given a custom
 * increment already found stored for an exercise (in either its current display unit, or the other
 * unit as a legacy-data fallback — see `UiExercise.checkEquipment`).
 *
 * Read-time repair of a value already written to the account. #4337 caught a stored 0, negative or
 * NaN; #5344 widened it to everything `isPlausibleEquipmentStep_kg` rejects — chiefly the leftover
 * of a round weight entered against an odd one. Either way the shared default is substituted, so an
 * invalid synthetic equipment is never built from stale bad data.
 *
 * Pass `workingWeight_kg` when the caller knows the weight this step has to load; see the predicate
 * for what it adds and why it is optional. Since #6295 the app's own default-sized step is kept even
 * when the working weight is that light; see `needsCustomWeightIncrementHeal`.
 */
export function resolveUsableCustomWeightIncrement_kg(
  storedIncrement_kg: number,
  incrementUnit: 'kg' | 'lb',
  workingWeight_kg?: number,
): number {
  return needsCustomWeightIncrementHeal(storedIncrement_kg, workingWeight_kg)
    ? defaultCustomWeightIncrement_kg(incrementUnit)
    : storedIncrement_kg;
}

/**
 * Decides which of an exercise's two per-unit stored custom weight increments (current display unit
 * vs the other unit) `UiExercise.checkEquipment` should use to build the temporary "automatic"
 * unbrowse equipment. Extracted so this decision is directly unit-testable, since `checkEquipment`
 * itself also builds UI-facing objects (translated names, etc.) that are awkward to unit test.
 *
 * Preference order:
 * 1. a genuinely usable value in the CURRENT unit
 * 2. a genuinely usable value in the OTHER unit — this also handles legacy data where `weight_unit`
 *    was undefined and the increment was stored under the wrong key, and it rescues a real
 *    other-unit step when the current unit's own value is corrupt (#4337: e.g. a stored 0)
 * 3. if NEITHER unit has a usable value, but at least one has SOME recorded value (even if corrupt),
 *    return that recorded value anyway — the caller still treats "has a custom increment" as true and
 *    heals the corrupt value via `resolveUsableCustomWeightIncrement_kg`, rather than falling through
 *    to a regular equipment lookup that cannot succeed anyway (the real equipment was already cleared
 *    by the original unbrowse)
 *
 * `workingWeight_kg` is the weight this exercise is actually trained at, when the caller knows it.
 * It is what lets the first two rules reject a step no machine could load — without it a stored
 * 80 kg step on an exercise trained at 80 kg is round enough to win rule 1, and a genuine step in
 * the other unit is discarded before the heal ever sees it.
 *
 * Returns `undefined` only when neither unit has ever had anything recorded at all.
 */
export function resolveCustomWeightIncrementSource(
  currentUnitIncrement_kg: number | undefined,
  otherUnitIncrement_kg: number | undefined,
  workingWeight_kg?: number,
): { increment_kg: number; fromOtherUnit: boolean } | undefined {
  if (isPlausibleEquipmentStep_kg(currentUnitIncrement_kg, workingWeight_kg)) {
    return { increment_kg: currentUnitIncrement_kg, fromOtherUnit: false };
  }
  if (isPlausibleEquipmentStep_kg(otherUnitIncrement_kg, workingWeight_kg)) {
    return { increment_kg: otherUnitIncrement_kg, fromOtherUnit: true };
  }
  if (currentUnitIncrement_kg !== undefined) {
    return { increment_kg: currentUnitIncrement_kg, fromOtherUnit: false };
  }
  if (otherUnitIncrement_kg !== undefined) {
    return { increment_kg: otherUnitIncrement_kg, fromOtherUnit: true };
  }
  return undefined;
}

// structural minimum needed from LoggingService — kept minimal so a plain, non-Angular class (like
// UiExercise) can report without needing Angular DI. Matches the same pattern as
// this file's sibling util/orphan-subslot-reporter.ts's OrphanReporter; the real LoggingService
// satisfies this shape.
export interface SilentProblemReporter {
  trackSilentProblem(name: string, context: any): void;
}

// #4337: dedup so each already-corrupted exercise+unit is reported at most once per process / tab
// session — checkEquipment runs many times per session (on every set click, workout load, unit
// switch, etc.), and one Sentry event per corrupted exercise is enough to tell how many users are
// still carrying pre-fix bad data.
const reportedHealedCustomIncrements = new Set<string>();

/**
 * Reports (once per exercise+unit per session) when `checkEquipment` is about to heal an
 * already-corrupted stored custom weight increment — #4337's 0, negative or NaN, and since #5344
 * anything else `isPlausibleEquipmentStep_kg` rejects. Call BEFORE resolving the usable increment,
 * since
 * `resolveUsableCustomWeightIncrement_kg`'s return value alone can't tell the caller whether a
 * substitution happened. A no-op when `storedIncrement_kg` is already usable.
 */
export function reportHealedCustomWeightIncrementIfNeeded(
  exerciseId: string,
  incrementUnit: 'kg' | 'lb',
  storedIncrement_kg: number,
  logging: SilentProblemReporter,
  workingWeight_kg?: number,
): void {
  if (!needsCustomWeightIncrementHeal(storedIncrement_kg, workingWeight_kg)) return;
  const dedupKey = `${exerciseId}:${incrementUnit}`;
  if (reportedHealedCustomIncrements.has(dedupKey)) return;
  reportedHealedCustomIncrements.add(dedupKey);
  // Stays on the PROBLEM tier despite the "-healed" name (#4382). The substitution the
  // caller does next is a read-time patch only — THIS path does not write the usable value
  // back. Two later steps may: the boot migration `repairImplausibleCustomWeightIncrements`
  // (roundness only, so never the whole-weight family), and since #6295 the workout page,
  // which saves the substituted step through `applyPendingCustomIncrementHeals`. Neither has
  // happened yet when this line runs, and the page save can fail or never run (a loader call
  // with no workout page), so the value is still unresolved here and `trackSilentInfo` would
  // be wrong.
  logging.trackSilentProblem('workout.equipment-custom-increment-healed', {
    exerciseId,
    incrementUnit,
    storedIncrement_kg,
  });
}

/**
 * A step the read-time heal replaced in memory, waiting for the workout page to save it (#6295).
 * `storedIncrement_kg` is the bad value the heal saw, kept so the save can tell whether something
 * else wrote that key in the meantime.
 */
export interface PendingCustomIncrementHeal {
  exerciseId: string;
  unit: 'kg' | 'lb';
  storedIncrement_kg: number;
  healedIncrement_kg: number;
}

/**
 * Writes read-time heals back into `fitnessState`, so a step only the page can recognise as bad
 * (the whole-weight family: 33 kg on an exercise trained at 30 kg) is repaired for good instead of
 * being patched and reported on every app start (#6295).
 *
 * A heal is written only when the key still holds the exact value the heal saw. Anything else means
 * a newer write landed in between — an unbrowse at set conclusion, or a sync — and that value wins.
 * NaN is compared as NaN, because a stored NaN is one of the values the heal repairs.
 *
 * Replaces, never deletes the key: `UiExercise.checkEquipment` builds the temporary equipment only
 * when a value is recorded (see `repairImplausibleCustomWeightIncrements` for why).
 *
 * Returns the heals actually written, for the caller to save and report.
 */
export function applyPendingCustomIncrementHeals(
  fitnessState: FitnessState,
  heals: PendingCustomIncrementHeal[],
): PendingCustomIncrementHeal[] {
  const written: PendingCustomIncrementHeal[] = [];
  for (const heal of heals) {
    const incrementByUnit = fitnessState.exerciseStats?.[heal.exerciseId]?.customWeightIncrementByUnit;
    if (!incrementByUnit) continue;
    // if the key changed since the heal saw it, a newer write owns it
    if (!Object.is(incrementByUnit[heal.unit], heal.storedIncrement_kg)) continue;
    incrementByUnit[heal.unit] = heal.healedIncrement_kg;
    written.push(heal);
  }
  return written;
}

// test-only: reset the dedup set so each test starts clean
export function _resetCustomIncrementHealReportDedupForTesting(): void {
  reportedHealedCustomIncrements.clear();
}
